/**
 * Track Hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackService, audioFeaturesService } from '../services/tracks';
import { playlistKeys } from './usePlaylists';
import type { TrackSearchParams, AnalyzeTracksRequest, LikedTracksParams } from '../types';

export const trackKeys = {
  all: ['tracks'] as const,
  search: (params: TrackSearchParams) => [...trackKeys.all, 'search', params] as const,
  detail: (id: string) => [...trackKeys.all, 'detail', id] as const,
  liked: (params?: LikedTracksParams) => [...trackKeys.all, 'liked', params] as const,
  areLiked: (ids: string[]) => [...trackKeys.all, 'areLiked', ids.sort().join(',')] as const,
  features: (id: string) => [...trackKeys.all, 'features', id] as const,
  featuresBatch: (ids: string[]) => [...trackKeys.all, 'features', 'batch', ids.sort().join(',')] as const,
};

/**
 * Hook to search Spotify catalog
 */
export function useTrackSearch(params: TrackSearchParams, enabled: boolean = true) {
  return useQuery({
    queryKey: trackKeys.search(params),
    queryFn: () => trackService.search(params),
    enabled: enabled && params.q.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get track details
 */
export function useTrack(trackId: string | undefined) {
  return useQuery({
    queryKey: trackKeys.detail(trackId!),
    queryFn: () => trackService.get(trackId!),
    enabled: !!trackId,
  });
}

/**
 * Hook to get user's liked songs
 */
export function useLikedTracks(params?: LikedTracksParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: trackKeys.liked(params),
    queryFn: () => trackService.getLiked(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to analyze tracks (get audio features)
 */
export function useAnalyzeTracks() {
  return useMutation({
    mutationFn: (request: AnalyzeTracksRequest) => trackService.analyze(request),
  });
}

/**
 * Hook to get cached audio features for a single track
 */
export function useAudioFeatures(trackId: string | undefined) {
  return useQuery({
    queryKey: trackKeys.features(trackId!),
    queryFn: () => audioFeaturesService.get(trackId!),
    enabled: !!trackId,
    staleTime: Infinity, // Features don't change
  });
}

/**
 * Hook to get audio features for multiple tracks
 */
export function useBatchAudioFeatures(ids: string[], enabled: boolean = true) {
  return useQuery({
    queryKey: trackKeys.featuresBatch(ids),
    queryFn: () => audioFeaturesService.getBatch(ids),
    enabled: enabled && ids.length > 0,
    staleTime: Infinity, // Features don't change
  });
}

/**
 * Add tracks to Liked Songs with optimistic update
 * Note: Changes are reflected immediately in UI, allowing instant re-like of accidentally unliked tracks
 */
export function useLikeTracks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trackIds: string[]) => trackService.likeTracks(trackIds),

    // Optimistic update - instant UI feedback
    onMutate: async (trackIds) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: trackKeys.all });

      // Snapshot current state for rollback
      const previousQueries = new Map();

      // Update ALL areLiked queries that contain these tracks
      queryClient.setQueriesData(
        { queryKey: trackKeys.all },
        (oldData: any) => {
          if (!oldData?.results) return oldData;

          // Store previous data for rollback
          previousQueries.set(oldData, oldData);

          // Update isLiked status for matching tracks
          return {
            ...oldData,
            results: oldData.results.map((item: { id: string; isLiked: boolean }) =>
              trackIds.includes(item.id) ? { ...item, isLiked: true } : item
            ),
          };
        }
      );

      return { previousQueries };
    },

    // Rollback on error
    onError: (_err, _trackIds, _context) => {
      // Invalidate all areLiked queries to refetch
      queryClient.invalidateQueries({ queryKey: trackKeys.all });
    },

    // Mark Liked Songs playlist as stale so it refreshes next time it's opened
    // Use refetchType: 'none' to prevent immediate refetch while viewing the list
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: playlistKeys.detail('liked-songs'),
        refetchType: 'none' // Don't refetch immediately, only on next mount
      });
    },
  });
}

/**
 * Remove tracks from Liked Songs with optimistic update
 * Note: Track remains visible in list with outline heart until page refresh,
 * allowing user to undo accidental unlikes
 */
export function useUnlikeTracks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (trackIds: string[]) => trackService.unlikeTracks(trackIds),

    onMutate: async (trackIds) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: trackKeys.all });

      // Update ALL areLiked queries that contain these tracks
      queryClient.setQueriesData(
        { queryKey: trackKeys.all },
        (oldData: any) => {
          if (!oldData?.results) return oldData;

          // Update isLiked status for matching tracks (heart becomes outline)
          return {
            ...oldData,
            results: oldData.results.map((item: { id: string; isLiked: boolean }) =>
              trackIds.includes(item.id) ? { ...item, isLiked: false } : item
            ),
          };
        }
      );

      return {};
    },

    onError: (_err, _trackIds, _context) => {
      // Invalidate all areLiked queries to refetch on error
      queryClient.invalidateQueries({ queryKey: trackKeys.all });
    },

    // Mark Liked Songs playlist as stale so it refreshes next time it's opened
    // Use refetchType: 'none' to prevent immediate refetch while viewing the list
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: playlistKeys.detail('liked-songs'),
        refetchType: 'none' // Don't refetch immediately, only on next mount
      });
    },

    // Note: Track stays in current view until you switch playlists
    // This allows user to re-like if it was accidental
  });
}

/**
 * Check if tracks are liked (batch)
 * Called when loading a regular playlist to populate isLiked status
 */
export function useAreLiked(trackIds: string[], options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: trackKeys.areLiked(trackIds),
    queryFn: () => trackService.areLiked(trackIds),
    enabled: options?.enabled !== false && trackIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  });
}
